import React from 'react';
import { WorkflowRule } from '../../../../types/workflowRules';
import { CheckCircle, AlertTriangle } from 'lucide-react';

interface WizardStep4Props {
  formData: Partial<WorkflowRule>;
  updateFormData: (updates: Partial<WorkflowRule>) => void;
  onNext: () => void;
  onBack: () => void;
}

/**
 * WizardStep4Review Component
 *
 * Final step of the workflow rule wizard. Displays a summary
 * of all configured settings for review before saving.
 *
 * @component
 */
export const WizardStep4Review: React.FC<WizardStep4Props> = ({
  formData
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Review & Confirm
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Review your workflow rule before saving
        </p>
      </div>

      {/* Rule Summary */}
      <div className="space-y-4">
        {/* Basic Info */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Basic Information</h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">Name:</dt>
              <dd className="font-medium text-gray-900 dark:text-white">{formData.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">Object:</dt>
              <dd className="font-medium text-gray-900 dark:text-white">{formData.objectApiName}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">Status:</dt>
              <dd className="flex items-center gap-2">
                {formData.isActive ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-green-600 dark:text-green-400">Active</span>
                  </>
                ) : (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-gray-400" />
                    <span className="text-gray-500">Inactive</span>
                  </>
                )}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">Trigger:</dt>
              <dd className="text-gray-900 dark:text-white">
                {formData.triggerType === 'onCreateOnly' && 'When created'}
                {formData.triggerType === 'onUpdateOnly' && 'When updated'}
                {formData.triggerType === 'onCreateAndUpdate' && 'When created or updated'}
              </dd>
            </div>
            {formData.description && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400 mb-1">Description:</dt>
                <dd className="text-gray-900 dark:text-white">{formData.description}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Criteria */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Criteria</h4>
          {formData.evaluationCriteria && formData.evaluationCriteria.conditions.length > 0 ? (
            <div className="text-sm">
              <div className="font-medium mb-2">
                Logic: {formData.evaluationCriteria.operator}
              </div>
              <div className="space-y-1">
                {formData.evaluationCriteria.conditions.map((condition, index) => (
                  <div key={condition.id} className="text-gray-600 dark:text-gray-400">
                    {index + 1}. {condition.fieldApiName} {condition.operator} {String(condition.value)}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              No conditions defined yet
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Actions</h4>
          {formData.actions && formData.actions.length > 0 ? (
            <div className="space-y-2 text-sm">
              {formData.actions.map((action, index) => (
                <div key={action.id} className="text-gray-600 dark:text-gray-400">
                  {index + 1}. {action.type}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              No actions defined yet
            </div>
          )}
        </div>
      </div>

      {/* Warnings */}
      {(!formData.evaluationCriteria?.conditions.length || !formData.actions?.length) && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <div className="font-medium mb-1">Incomplete Rule</div>
              <div>
                {!formData.evaluationCriteria?.conditions.length && 'No criteria defined. '}
                {!formData.actions?.length && 'No actions defined. '}
                The rule will be saved but won't execute until criteria and actions are added.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Important Notes */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <div className="font-medium mb-2">Important Notes:</div>
          <ul className="list-disc list-inside space-y-1">
            {formData.isActive && (
              <li>This rule will be active immediately after saving</li>
            )}
            <li>You can edit this rule anytime from the Workflow Rules list</li>
            <li>You can add or modify criteria and actions later</li>
            <li>Rule execution history will be tracked for monitoring</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
